import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, Divider, Form, Skeleton } from 'antd';

import { PageProps } from '@/types/PageProps';

import TabContentWrapper from '@/components/_common/TabContentWrapper';
import { SaveButton } from '@/components/_common/Buttons';
import BankingEntryForm from '@/components/Financial/Accounts/BankingEntry/BankingEntryForm';
import {
  useBankingEntry,
  useReversalBankingEntry,
  useUpdateBankingEntry,
} from '@/core/domain/financial/BankingEntry/BankingEntry.hooks';
import {
  BankingEntryFormValues,
  BankingEntryReversalFormValues,
  BankingEntryReversalRequest,
  BankingEntryUpdateRequest,
} from '@/core/domain/financial/BankingEntry/BankingEntry.types';
import { useHistory, useRouteMatch } from 'react-router-dom';
import moment from 'moment';
import BankingEntryReversalModal from '@/components/Financial/BankingEntry/BankingEntryReversalModal';
import { useDisclosure } from '@/hooks';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { DateTimeFormat } from '@/utils/moment';
import ButtonBack from '@/components/_common/ButtonBack';
import handleFormChange from '@/hooks/handleFormChange';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import usePageSearchHistory from '@/hooks/pageSearchHistory';
import EmployeePopoverGenerateReceipt from '@/components/_common/EmployeePopoverGenerateReceipt';

function BankingEntryDetailsPage({ route }: PageProps) {
  const intl = useIntl();
  const history = useHistory();
  const [form] = Form.useForm<BankingEntryFormValues>();
  const { params } = useRouteMatch<{ bankingEntryId: string }>();
  const [formChange, setFormChange] = useState({});
  const disabled = !Object.keys(formChange).some((key) => !!key);

  const companyId = Form.useWatch('companyId', form);

  const { bankingEntryId } = useRouteMatch<{
    bankingEntryId: string;
  }>().params;

  const [modalReversal, modalReversalAction] = useDisclosure();

  const bankingEntry = useBankingEntry(Number(bankingEntryId));

  const updateMutation = useUpdateBankingEntry();
  const reversalMutation = useReversalBankingEntry();

  useEffect(() => {
    if (bankingEntry.data) {
      form.setFieldsValue({
        ...bankingEntry.data,
        launchDate: moment(bankingEntry.data.launchDate),
        conciliationDate: bankingEntry.data.conciliationDate
          ? moment(bankingEntry.data.conciliationDate)
          : undefined,
      });
    }
  }, [bankingEntry.dataUpdatedAt]);

  const handleSaveClick = async () => {
    const values = await form.validateFields();

    const obj: BankingEntryUpdateRequest = {
      ...values,
      launchDate: values.launchDate?.format(DateTimeFormat),
      conciliationDate: values.conciliationDate?.format(DateTimeFormat),
      id: Number(bankingEntryId),
    };

    await updateMutation.mutateAsync(obj);
  };

  const handleReversalBankingEntry = async (
    values?: BankingEntryReversalFormValues,
  ) => {
    if (bankingEntry.data && values) {
      const obj: BankingEntryReversalRequest = {
        ...values,
        id: bankingEntry.data.id,
        reversalDate: values.reversalDate?.format(DateTimeFormat),
      };

      await reversalMutation.mutateAsync(obj);

      modalReversalAction.close();
    }
  };

  const { getSearch } = usePageSearchHistory();
  const path = '/app/financial/bankingEntry';
  const pathWithSearch = path + getSearch(path);

  return (
    <>
      <TabContentWrapper
        title={route.name}
        permissions={route.permissions}
        extra={
          <SpaceForButton>
            <Button
              block
              type="primary"
              onClick={modalReversalAction.open}
              disabled={
                bankingEntry.data?.reversed ||
                !hasPermissions([
                  Authorities.FINANCIAL_BANKING_ENTRIES_REVERSE,
                  Authorities.FINANCIAL_BANKING_ENTRIES_EDIT,
                ])
              }
              loading={reversalMutation.isLoading}
            >
              Estornar lançamento bancário
            </Button>

            <SaveButton
              block
              type="primary"
              onClick={handleSaveClick}
              loading={updateMutation.isLoading}
              disabled={
                !hasPermissions([Authorities.FINANCIAL_BANKING_ENTRIES_EDIT])
              }
            >
              {intl.formatMessage({ id: 'commons.form.button.save' })}
            </SaveButton>
          </SpaceForButton>
        }
      >
        <Skeleton loading={bankingEntry.isFetching} active>
          <Form.Provider
            onFormChange={(name, info) =>
              handleFormChange(name, info, setFormChange)
            }
          >
            <BankingEntryForm
              isEdit
              form={form}
              bankingEntry={bankingEntry.data}
            />
          </Form.Provider>

          <Divider />

          <SpaceForButton>
            <ButtonBack path={pathWithSearch} disabled={disabled} />

            <Button
              block
              type="primary"
              onClick={() => history.push('/app/financial/bankingEntry/create')}
              disabled={
                !hasPermissions([Authorities.FINANCIAL_BANKING_ENTRIES_ADD])
              }
            >
              Novo Lançamento
            </Button>

            <EmployeePopoverGenerateReceipt
              bankEntryId={+params.bankingEntryId}
              companyId={companyId}
              generateDirectly
              nature={bankingEntry.data?.nature}
            >
              <Button block type="primary" key="viewReport">
                Imprimir Recibo
              </Button>
            </EmployeePopoverGenerateReceipt>
          </SpaceForButton>
        </Skeleton>
      </TabContentWrapper>

      <BankingEntryReversalModal
        open={modalReversal}
        onCancel={modalReversalAction.close}
        bankingEntryDetail={bankingEntry.data}
        handleOkDetail={handleReversalBankingEntry}
        loadingDetail={reversalMutation.isLoading}
      />
    </>
  );
}

export default BankingEntryDetailsPage;
